import { useLoaderData } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ChevronLink from '../../components/common/ChevronLink/ChevronLink';
import IconText from '../../components/common/IconText/IconText';
import Card from '../../components/common/Card/Card';
import { Colors } from '../../Constants';
import Intro from '../../components/common/Intro/Intro';
import { capitalizeFirstLetter } from '../../utils/texts';
import { PartiesDataResponseType } from '../../types';

function Parties() {
    const { t } = useTranslation();

    const response = useLoaderData() as [{ data: any }, { data: any }, { data: any }];
    const basicData = response[0].data;
    const partiesData = response[1].data as PartiesDataResponseType[];
    const partiesStats = response[2].data;

    const cardComponent = (partyData: {
        strana_zkratka: string;
        pocet_aktivnich: number;
        strana_barva: string;
        strana_id: number;
    }) => {
        const stats = partiesStats.find((p: { strana_zkratka: any }) => p.strana_zkratka === partyData.strana_zkratka);
        const isInCoalition = basicData.koalice.find(
            (koaliceParty: { strana_zkratka: string }) => koaliceParty.strana_zkratka === partyData.strana_zkratka
        );
        const isInactive = partyData.pocet_aktivnich === 0;

        let mandateTranslation;
        if (partyData.pocet_aktivnich === 1) {
            mandateTranslation = t('mandate_one');
        } else if (partyData.pocet_aktivnich >= 2 && partyData.pocet_aktivnich <= 4) {
            mandateTranslation = t('mandate_few');
        } else {
            mandateTranslation = t('mandate_other');
        }

        const commonColorProps = { iconAndHighlightColor: isInactive ? Colors.darkGray : partyData.strana_barva };

        return (
            <Card key={partyData.strana_id} className='card-list-item' link={`${partyData.strana_id}`}>
                <div className='link-container'>
                    <h4 style={{ color: partyData.strana_barva }}>{partyData.strana_zkratka}</h4>
                    <IconText
                        icon={isInactive ? 'unpublished' : isInCoalition ? 'handshake' : 'thumbs_up_down'}
                        text={`${
                            isInactive
                                ? capitalizeFirstLetter(t('inactive'))
                                : isInCoalition
                                ? t('coalition')
                                : t('opposition')
                        }`}
                        highlightSubString={
                            isInactive
                                ? capitalizeFirstLetter(t('inactive'))
                                : isInCoalition
                                ? t('coalition')
                                : t('opposition')
                        }
                        {...commonColorProps}
                    />
                </div>
                <div className='card-list-item-content'>
                    <IconText
                        icon='chair'
                        text={`${partyData.pocet_aktivnich} ${mandateTranslation}`}
                        highlightSubString={`${partyData.pocet_aktivnich}`}
                        {...commonColorProps}
                    />
                    <div className='link-container'>
                        <IconText
                            icon='group'
                            text={`${stats.celkova_ucast}% ${t('participation')}`}
                            highlightSubString={`${stats.celkova_ucast.toFixed(2)}%`}
                            {...commonColorProps}
                        />
                        <ChevronLink text={t('details')} chevronDirection='right' linkDisabled />
                    </div>
                </div>
            </Card>
        );
    };

    if (partiesData !== undefined) {
        // Sort partiesData by coalition status and number of mandates
        const sortedPartiesData = [...partiesData].sort((a, b) => {
            const isInCoalitionA = basicData.koalice.some(
                (koaliceParty: { strana_zkratka: string }) => koaliceParty.strana_zkratka === a.strana_zkratka
            );
            const isInCoalitionB = basicData.koalice.some(
                (koaliceParty: { strana_zkratka: string }) => koaliceParty.strana_zkratka === b.strana_zkratka
            );

            if (isInCoalitionA && !isInCoalitionB) {
                return -1;
            } else if (!isInCoalitionA && isInCoalitionB) {
                return 1;
            } else {
                return b.pocet_aktivnich - a.pocet_aktivnich;
            }
        });

        return (
            <>
                <Intro
                    title={t('Parties')}
                    subtitle={`${basicData.funkcni_obdobi}`}
                    description={`${t('parties-in-term', {
                        term: basicData.funkcni_obdobi,
                        parties_count: partiesData.length,
                        party: sortedPartiesData[0].strana_zkratka
                    })}`}
                />
                <div className='card-list'>{sortedPartiesData.map((party) => cardComponent(party))}</div>
            </>
        );
    }

    return null;
}

export default Parties;
